import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updatePassword } from 'firebase/auth';
import { auth, db, storage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { INDUSTRIES } from '@/config/industries';
import type { IndustryId } from '@/config/industries';

export function SettingsPage() {
  const { appUser, refreshAppUser, canWriteCompanyData } = useAuth();
  const [name, setName] = useState('');
  const [industryId, setIndustryId] = useState<IndustryId | ''>('');
  const [country, setCountry] = useState('MX');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null);

  const LOGO_RECOMMENDED = { width: 240, height: 80 };
  const LOGO_MAX = { width: 400, height: 120 };
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  useEffect(() => {
    if (!appUser?.companyId) {
      setLoaded(true);
      return;
    }
    getDoc(doc(db, 'companies', appUser.companyId)).then((snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setName(d.name ?? '');
        const ind = d.industry ?? '';
        setIndustryId(INDUSTRIES.some((i) => i.id === ind) ? (ind as IndustryId) : '');
        setCountry(d.country ?? 'MX');
        setCurrentLogoUrl((d.branding?.logoUrl as string) ?? null);
      }
      setLoaded(true);
    });
  }, [appUser?.companyId]);

  const validateLogoDimensions = (file: File): Promise<{ valid: boolean; error?: string }> => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        if (img.width > LOGO_MAX.width || img.height > LOGO_MAX.height) {
          resolve({
            valid: false,
            error: `El logo no debe superar ${LOGO_MAX.width}×${LOGO_MAX.height} px. Actual: ${img.width}×${img.height} px.`,
          });
        } else {
          resolve({ valid: true });
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ valid: false, error: 'No se pudo leer la imagen.' });
      };
      img.src = url;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canWriteCompanyData) return;
    if (!appUser?.companyId) return;
    setLogoError(null);
    setSaving(true);
    try {
      let logoUrl: string | undefined;
      if (logoFile) {
        const validation = await validateLogoDimensions(logoFile);
        if (!validation.valid) {
          setLogoError(validation.error ?? 'Dimensiones no válidas.');
          setSaving(false);
          return;
        }
        const path = `companies/${appUser.companyId}/logo_${Date.now()}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, logoFile);
        logoUrl = await getDownloadURL(storageRef);
      }
      const companyRef = doc(db, 'companies', appUser.companyId);
      const current = (await getDoc(companyRef)).data() ?? {};
      await setDoc(
        companyRef,
        {
          name,
          industry: industryId || null,
          country: country || 'MX',
          branding: {
            ...current.branding,
            ...(logoUrl && { logoUrl }),
          },
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      await refreshAppUser();
      if (logoUrl) {
        setCurrentLogoUrl(logoUrl);
        setLogoFile(null);
      }
    } catch (err) {
      console.error('Error al guardar:', err);
      const msg = err instanceof Error ? err.message : 'Error al guardar. Revisa que Storage esté habilitado y las reglas desplegadas.';
      setLogoError(msg);
      window.alert(`Error: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);
    if (password.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden.');
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setPasswordError('No hay sesión activa.');
      return;
    }
    setPasswordSaving(true);
    try {
      await updatePassword(user, password);
      setPassword('');
      setConfirmPassword('');
      setPasswordSuccess(true);
    } catch (err) {
      console.error('Error cambiando contraseña', err);
      setPasswordError(err instanceof Error ? err.message : 'Error al guardar la contraseña.');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (!appUser?.companyId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">Ajustes</h1>
        <p className="text-muted-foreground">No tienes empresa asignada.</p>
      </div>
    );
  }

  if (!loaded) return <p className="text-muted-foreground">Cargando...</p>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Ajustes</h1>
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Datos de empresa</CardTitle>
          <CardDescription>Nombre, rubro y logo para reportes.</CardDescription>
          {!canWriteCompanyData && (
            <p className="text-xs text-muted-foreground">
              Tu rol es solo lectura: no puedes modificar la configuración de empresa.
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre de la empresa</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} disabled={!canWriteCompanyData} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">País</Label>
              <select
                id="country"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={!canWriteCompanyData}
              >
                <option value="MX">México</option>
                <option value="PE">Perú</option>
                <option value="CO">Colombia</option>
                <option value="ES">España</option>
                <option value="AR">Argentina</option>
                <option value="CL">Chile</option>
                <option value="US">Estados Unidos</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Afecta normas psicométricas y normativas locales (ej. NOM-035).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="industry">Tipo de empresa (rubro)</Label>
              <select
                id="industry"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={industryId}
                onChange={(e) => setIndustryId(e.target.value as IndustryId | '')}
                disabled={!canWriteCompanyData}
              >
                <option value="">Selecciona un rubro</option>
                {INDUSTRIES.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Usado para preseleccionar la batería recomendada al crear procesos.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Logo de la empresa (opcional)</Label>
              <p className="text-xs text-muted-foreground">
                Recomendado: {LOGO_RECOMMENDED.width}×{LOGO_RECOMMENDED.height} px. Máximo: {LOGO_MAX.width}×{LOGO_MAX.height} px.
                Se mostrará en todos los reportes junto al logo de PsyTalent.
              </p>
              {currentLogoUrl && (
                <div className="flex items-center gap-3 py-2">
                  <img
                    src={currentLogoUrl}
                    alt="Logo actual"
                    className="h-10 max-w-[120px] object-contain border rounded"
                  />
                  <span className="text-sm text-muted-foreground">Logo actual</span>
                </div>
              )}
              {logoError && (
                <p className="text-sm text-destructive">{logoError}</p>
              )}
              <Input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={!canWriteCompanyData}
                onChange={(e) => {
                  setLogoFile(e.target.files?.[0] ?? null);
                  setLogoError(null);
                }}
              />
            </div>
            <Button type="submit" disabled={saving || !canWriteCompanyData}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </form>
        </CardContent>
      </Card>
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Contraseña</CardTitle>
          <CardDescription>
            Establece o cambia tu contraseña para iniciar sesión con correo y contraseña.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {passwordError && (
              <p className="text-sm text-destructive">{passwordError}</p>
            )}
            {passwordSuccess && (
              <p className="text-sm text-green-600">Contraseña guardada correctamente.</p>
            )}
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nueva contraseña</Label>
              <Input
                id="newPassword"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                minLength={6}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmNewPassword">Repetir contraseña</Label>
              <Input
                id="confirmNewPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contraseña"
                minLength={6}
                autoComplete="new-password"
              />
            </div>
            <Button type="submit" disabled={passwordSaving}>
              {passwordSaving ? 'Guardando...' : 'Establecer / Cambiar contraseña'}
            </Button>
          </form>
        </CardContent>
      </Card>
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Otras opciones</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Button variant="outline" asChild>
            <Link to="/app/settings/policies">Políticas y consentimientos</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
